import { ThemeEvents } from '@theme/events';

/**
 * Hide/show product media thumbnails and slides based on the active variant option value.
 *
 * Assumption: media alt text contains the variant color string (e.g. "Black", "Tortoise").
 * We keep all elements in the DOM so slide indexes remain stable; we only toggle `hidden`.
 *
 * Added by evan@cooeecommerce.com to display only selected variant images.
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalize(value) {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * @param {any} variant
 * @param {string[]} availableAltsNormalized
 * @returns {string | null} normalized match value
 */
function pickAltMatchFromVariant(variant, availableAltsNormalized) {
  if (!variant) return null;

  // This shop uses Option 1 as "Color"
  const color = normalize(variant.option1);
  if (!color) return null;
  return availableAltsNormalized.includes(color) ? color : null;
}

/**
 * @param {HTMLElement[]} elements
 * @param {string | null} match
 */
function applyAltMatch(elements, match) {
  if (!elements.length) return;

  if (!match) {
    elements.forEach((el) => el.removeAttribute('hidden'));
    return;
  }

  let visibleCount = 0;

  for (const el of elements) {
    const isMatch = normalize(el.dataset.mediaAlt) === match;
    if (isMatch) {
      el.removeAttribute('hidden');
      visibleCount += 1;
    } else {
      el.setAttribute('hidden', 'true');
    }
  }

  if (visibleCount === 0) {
    elements.forEach((el) => el.removeAttribute('hidden'));
  }
}

/**
 * @param {HTMLElement[]} elements
 * @param {any} variant
 */
function filterElementsByVariantAlt(elements, variant) {
  if (!elements.length) return;

  const altsNormalized = elements
    .map((el) => normalize(el.dataset.mediaAlt))
    .filter(Boolean);

  const match = pickAltMatchFromVariant(variant, altsNormalized);
  applyAltMatch(elements, match);
}

/**
 * @param {Document | HTMLElement} root
 * @param {any} variant
 */
function filterAllThumbnailControls(root, variant) {
  const controls = Array.from(root.querySelectorAll('slideshow-controls[thumbnails]'));

  for (const control of controls) {
    const buttons = /** @type {HTMLButtonElement[]} */ (
      Array.from(control.querySelectorAll('button.slideshow-controls__thumbnail'))
    );

    filterElementsByVariantAlt(buttons, variant);
  }
}

/**
 * @param {Document | HTMLElement} root
 * @param {any} variant
 */
function filterZoomDialogs(root, variant) {
  const dialogs = Array.from(root.querySelectorAll('zoom-dialog'));

  for (const dialog of dialogs) {
    const buttons = /** @type {HTMLElement[]} */ (
      Array.from(dialog.querySelectorAll('button.dialog-thumbnails-list__thumbnail'))
    );
    const lis = /** @type {HTMLElement[]} */ (
      Array.from(dialog.querySelectorAll('ul.dialog-zoomed-gallery > li'))
    );
    const altsSource = buttons.length ? buttons : lis;
    if (!altsSource.length) continue;

    const altsNormalized = altsSource
      .map((el) => normalize(/** @type {HTMLElement} */ (el).dataset.mediaAlt))
      .filter(Boolean);

    const match = pickAltMatchFromVariant(variant, altsNormalized);
    applyAltMatch(buttons, match);
    applyAltMatch(lis, match);
  }
}

/**
 * @param {Document | HTMLElement} root
 * @param {any} variant
 */
function filterProductMediaGallerySlides(root, variant) {
  const slides = /** @type {HTMLElement[]} */ (
    Array.from(root.querySelectorAll('media-gallery slideshow-component slideshow-slide[data-media-alt]'))
  );

  filterElementsByVariantAlt(slides, variant);
}

/**
 * @param {Document | HTMLElement} root
 * @param {any} variant
 */
function filterProductMediaGridItems(root, variant) {
  const items = /** @type {HTMLElement[]} */ (
    Array.from(root.querySelectorAll('media-gallery ul.media-gallery__grid > li[data-media-alt]'))
  );

  filterElementsByVariantAlt(items, variant);
}

/**
 * @param {Document | HTMLElement} root
 * @param {any} variant
 */
/**
 * After hiding slides, snap the product gallery carousel to the first visible slide so the
 * scroller is not left at a scroll position that still shows removed (non-variant) content.
 *
 * @param {Document | HTMLElement} root
 */
function snapProductGalleriesToFirstVisibleSlide(root) {
  const components = root.querySelectorAll('media-gallery slideshow-component');

  for (const el of components) {
    const slideshow = /** @type {{ select: Function; slides?: HTMLElement[] }} */ (el);
    if (!slideshow.slides?.length || typeof slideshow.select !== 'function') continue;
    void slideshow.select(0, undefined, { animate: false });
  }
}

function filterAllVariantMedia(root, variant) {
  filterAllThumbnailControls(root, variant);
  filterZoomDialogs(root, variant);
  filterProductMediaGallerySlides(root, variant);
  filterProductMediaGridItems(root, variant);
  snapProductGalleriesToFirstVisibleSlide(root);
}

function getInitialVariantFromDom() {
  const jsonEl = document.querySelector('variant-picker script[type="application/json"]');
  if (!jsonEl?.textContent) return null;
  try {
    return JSON.parse(jsonEl.textContent);
  } catch {
    return null;
  }
}

// Initial render
filterAllVariantMedia(document, getInitialVariantFromDom());

// Variant change (after section HTML fetch/morph)
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const customEvent = /** @type {CustomEvent<{ resource?: unknown }>} */ (event);
  /** @type {any} */
  const variant = customEvent.detail?.resource ?? null;
  filterAllVariantMedia(document, variant);
});
